import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SlidersHorizontal, X } from "lucide-react";
import Select from "react-select";

interface OptionType {
  value: string;
  label: string;
}

interface ParticipantElementsFilterProps {
  show: boolean;
  onHide: () => void;
  caseworkerOptions: OptionType[];
  selectedCaseworkerIds: string[];
  onCaseworkerChange: (val: string[]) => void;
  waiverOptions: OptionType[];
  selectedWaiverIds: string[];
  onWaiverChange: (val: string[]) => void;
  regionOptions: OptionType[];
  selectedRegionId: number | null;
  onRegionChange: (val: string) => void;
  selectedEmailLinked: string;
  onEmailLinkedChange: (val: string) => void;
  onReset: () => void;
  onApply: () => void;
}

const emailLinkedOptions: OptionType[] = [
  { value: "", label: "All" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

// React Select still needs one inline positioning value because its menus are
// portaled to document.body. All visual styling is supplied through Tailwind.
const selectPositioningStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 10000 }),
};

const selectClassNames = {
  control: ({ isFocused, isDisabled }: any) =>
    `min-h-[42px] cursor-pointer rounded-lg border-[1px] bg-[#ffffff] transition-colors dark:bg-[#20232E] ${
      isFocused
        ? "border-[#7E57C2] ring-2 ring-[#7E57C2]/15 dark:border-[#9b78d3]"
        : "border-gray-200 hover:border-gray-300 dark:border-white/[0.09] dark:hover:border-white/[0.16]"
    } ${isDisabled ? "cursor-not-allowed bg-gray-50 opacity-60 dark:bg-[#171A24]" : ""}`,
  valueContainer: () => "gap-1 px-3 py-1",
  input: () =>
    "m-0 text-sm text-gray-900 dark:text-gray-100 [&_input]:!m-0 [&_input]:!border-0 [&_input]:!bg-transparent [&_input]:!p-0 [&_input]:!text-inherit [&_input]:!shadow-none [&_input]:!outline-none [&_input]:!ring-0",
  singleValue: () => "text-sm text-gray-900 dark:text-gray-100",
  placeholder: () => "text-sm text-gray-400 dark:text-gray-500",
  indicatorsContainer: () => "flex items-center text-gray-400 dark:text-gray-500",
  dropdownIndicator: () =>
    "flex p-2 transition-colors hover:text-gray-700 dark:hover:text-gray-200",
  clearIndicator: () =>
    "flex p-2 transition-colors hover:text-gray-700 dark:hover:text-gray-200",
  indicatorSeparator: () => "hidden",
  multiValue: () =>
    "my-0.5 flex items-center overflow-hidden rounded-md bg-[#F3F0FF] dark:bg-[#7E57C2]/20",
  multiValueLabel: () =>
    "px-2 py-1 text-xs font-medium text-[#7E57C2] dark:text-[#c4a4ff]",
  multiValueRemove: () =>
    "flex self-stretch items-center px-1.5 text-[#7E57C2] transition-colors hover:bg-[#7E57C2] hover:text-white dark:text-[#c4a4ff] dark:hover:bg-[#7E57C2] dark:hover:text-white",
  menu: () =>
    "mt-1 overflow-hidden rounded-lg border-[1px] border-gray-100 bg-[#ffffff] shadow-xl dark:border-white/[0.09] dark:bg-[#20232E]",
  menuList: () => "max-h-64 p-1",
  option: ({ isFocused, isSelected, isDisabled }: any) =>
    `cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
      isSelected
        ? "bg-[#7E57C2] text-white"
        : isFocused
          ? "bg-gray-100 text-gray-900 dark:bg-[#282C39] dark:text-gray-100"
          : "text-gray-700 dark:text-gray-200"
    } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`,
  noOptionsMessage: () => "px-3 py-4 text-sm text-gray-500 dark:text-gray-400",
};

const fieldLabelClass =
  "mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200";

const ParticipantElementsFilter: React.FC<ParticipantElementsFilterProps> = ({
  show,
  onHide,
  caseworkerOptions,
  selectedCaseworkerIds,
  onCaseworkerChange,
  waiverOptions,
  selectedWaiverIds,
  onWaiverChange,
  regionOptions,
  selectedRegionId,
  onRegionChange,
  selectedEmailLinked,
  onEmailLinkedChange,
  onReset,
  onApply,
}) => {
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );
  const drawerRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  const onHideRef = useRef(onHide);

  useEffect(() => {
    onHideRef.current = onHide;
  }, [onHide]);

  useEffect(() => {
    const root = document.documentElement;
    const syncDarkMode = () => setIsDarkMode(root.classList.contains("dark"));
    const observer = new MutationObserver(syncDarkMode);

    syncDarkMode();
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!show) return;

    previouslyFocusedElement.current = document.activeElement as HTMLElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onHideRef.current();
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) return;

      const focusableElements = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedElement.current?.focus();
    };
  }, [show]);

  if (!show || typeof document === "undefined") return null;

  const regionOptionsWithAll = [
    { value: "", label: "All" },
    ...regionOptions,
  ];

  const drawer = (
    <div className={`${isDarkMode ? "dark" : ""} fixed inset-0 z-[9998]`}>
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200"
        onMouseDown={onHide}
        aria-hidden="true"
      />

      <section
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="participant-filter-title"
        className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-[#ffffff] shadow-[-18px_0_50px_rgba(0,0,0,0.16)] animate-in slide-in-from-right duration-300 dark:bg-[#171A24] dark:text-gray-100 dark:shadow-[-18px_0_50px_rgba(0,0,0,0.5)]"
      >
        <header className="flex shrink-0 items-start justify-between border-b border-gray-100 px-5 py-5 dark:border-white/[0.07] sm:px-6">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#7E57C2]/10 text-[#7E57C2] dark:bg-[#7E57C2]/20 dark:text-[#c4a4ff]">
              <SlidersHorizontal size={18} aria-hidden="true" />
            </span>
            <div>
              <h2
                id="participant-filter-title"
                className="m-0 text-lg font-bold leading-tight text-gray-900 dark:text-gray-100"
              >
                Filters
              </h2>
              <p className="m-0 mt-1 text-sm text-gray-500 dark:text-gray-400">
                Narrow the participant and caseworker results.
              </p>
            </div>
          </div>

          <button
            ref={closeButtonRef}
            type="button"
            onClick={onHide}
            className="-mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/40 dark:hover:bg-white/[0.07] dark:hover:text-gray-100"
            aria-label="Close filters"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-6 scrollbar-thin sm:px-6">
          <div>
            <label htmlFor="caseworker-filter" className={fieldLabelClass}>
              Caseworker
            </label>
            <Select
              inputId="caseworker-filter"
              isMulti
              unstyled
              options={caseworkerOptions}
              value={caseworkerOptions.filter((option) =>
                selectedCaseworkerIds.includes(option.value),
              )}
              onChange={(selected) =>
                onCaseworkerChange(selected.map((option) => option.value))
              }
              classNamePrefix="participants-page-select"
              classNames={selectClassNames}
              placeholder="Select caseworker(s)"
              styles={selectPositioningStyles}
              menuPortalTarget={document.body}
            />
          </div>

          <div>
            <label htmlFor="waiver-filter" className={fieldLabelClass}>
              Participant Waiver
            </label>
            <Select
              inputId="waiver-filter"
              isMulti
              unstyled
              options={waiverOptions}
              value={waiverOptions.filter((option) =>
                selectedWaiverIds.includes(option.value),
              )}
              onChange={(selected) =>
                onWaiverChange(selected.map((option) => option.value))
              }
              classNamePrefix="participants-page-select"
              classNames={selectClassNames}
              placeholder="Select waiver(s)"
              styles={selectPositioningStyles}
              menuPortalTarget={document.body}
            />
          </div>

          <div>
            <label htmlFor="region-filter" className={fieldLabelClass}>
              Region
            </label>
            <Select
              inputId="region-filter"
              unstyled
              options={regionOptionsWithAll}
              value={
                selectedRegionId
                  ? regionOptionsWithAll.find(
                      (option) => option.value === selectedRegionId.toString(),
                    )
                  : regionOptionsWithAll[0]
              }
              onChange={(selected) => onRegionChange(selected?.value ?? "")}
              classNamePrefix="participants-page-select"
              classNames={selectClassNames}
              styles={selectPositioningStyles}
              menuPortalTarget={document.body}
            />
          </div>

          <div>
            <label htmlFor="email-linked-filter" className={fieldLabelClass}>
              Email Linked
            </label>
            <Select
              inputId="email-linked-filter"
              unstyled
              options={emailLinkedOptions}
              value={
                emailLinkedOptions.find(
                  (option) => option.value === selectedEmailLinked,
                ) ?? emailLinkedOptions[0]
              }
              onChange={(selected) =>
                onEmailLinkedChange(selected?.value ?? "")
              }
              classNamePrefix="participants-page-select"
              classNames={selectClassNames}
              styles={selectPositioningStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </div>

        <footer className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/70 px-5 py-4 dark:border-white/[0.07] dark:bg-[#11131C] sm:px-6">
          <button
            type="button"
            onClick={onReset}
            className="h-10 rounded-lg border-[1px] border-gray-200 bg-[#ffffff] px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/30 dark:border-white/[0.09] dark:bg-[#282C39] dark:text-gray-200 dark:hover:bg-[#333338]"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onApply}
            className="h-10 rounded-lg border-0 bg-[#7E57C2] px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#6A4AA8] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/40 focus-visible:ring-offset-2 dark:ring-offset-[#171A24]"
          >
            Apply filters
          </button>
        </footer>
      </section>
    </div>
  );

  return createPortal(drawer, document.body);
};

export default ParticipantElementsFilter;
